import { useEffect, useState } from "react";
import axios from "axios";
import { ensureCsrfToken } from "../api/csrf";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  withCredentials: true,
});
type View = {
  requestId: string;
  /** Absent pour un contrat. */
  documentKind?: string;
  documentName: string;
  documentHash: string;
  expiresAt: string;
  state: string;
  signer: {
    name: string;
    role: "student" | "guardian" | "school";
    state: string;
  };
  consents: {
    read: string;
    electronic: string;
    guardian: string;
    school: string;
  };
};
const roleName = {
  student: "Élève",
  guardian: "Représentant légal",
  school: "Responsable de l’auto-école",
};
/** Libellés de la page selon le document présenté. */
const WORDING = {
  contract: {
    header: "Permio · Signature de contrat",
    title: "Consultez votre contrat avant de signer",
    reopen:
      "Rouvrez le lien personnel reçu par e-mail pour accéder au contrat.",
    consult: "Consulter mon contrat",
    download: "Télécharger le contrat complet",
    frame: "Contrat complet et annexes",
    signed: "Contrat signé",
    sign: "Signer ce contrat",
    back: "Revenir au contrat",
    file: "contrat",
  },
  quote: {
    header: "Permio · Signature de devis",
    title: "Consultez votre devis avant de signer",
    reopen: "Rouvrez le lien personnel reçu par e-mail pour accéder au devis.",
    consult: "Consulter mon devis",
    download: "Télécharger le devis complet",
    frame: "Devis complet",
    signed: "Devis signé",
    sign: "Signer ce devis",
    back: "Revenir au devis",
    file: "devis",
  },
  order: {
    header: "Permio · Signature de commande",
    title: "Consultez votre commande avant de signer",
    reopen:
      "Rouvrez le lien personnel reçu par e-mail pour accéder à la commande.",
    consult: "Consulter ma commande",
    download: "Télécharger la commande complète",
    frame: "Commande complète",
    signed: "Commande signée",
    sign: "Signer cette commande",
    back: "Revenir à la commande",
    file: "commande",
  },
};
type Kind = keyof typeof WORDING;
const kindOf = (value?: string | null): Kind =>
  value === "quote" || value === "order" ? value : "contract";

export default function ContractSigningPage() {
  // Lien reçu par e-mail (« token ») ou code demandé depuis l'espace élève (« challenge »).
  const [access] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)),
  );
  const invite = access.get("token") ?? "";
  const fromPortal = !invite && access.has("challenge");
  const [kind, setKind] = useState<Kind>(() => kindOf(access.get("kind")));
  const w = WORDING[kind];
  const [challenge, setChallenge] = useState(
      () => access.get("challenge") ?? "",
    ),
    [destination, setDestination] = useState(() => access.get("email") ?? ""),
    [code, setCode] = useState("");
  const [session, setSession] = useState(""),
    [view, setView] = useState<View | null>(null),
    [pdf, setPdf] = useState("");
  const [read, setRead] = useState(false),
    [electronic, setElectronic] = useState(false),
    [authority, setAuthority] = useState(false),
    [name, setName] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [declineReview, setDeclineReview] = useState(false);
  useEffect(() => {
    window.history.replaceState({}, "", window.location.pathname);
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  useEffect(
    () => () => {
      if (pdf) URL.revokeObjectURL(pdf);
    },
    [pdf],
  );
  const headers = (credential = session) => ({
    Authorization: `Bearer ${credential}`,
  });
  async function post(path: string, data: unknown, credential = session) {
    const csrf = await ensureCsrfToken();
    return api.post(`/v1/contract-signing/${path}`, data, {
      headers: { ...headers(credential), "x-csrf-token": csrf ?? "" },
    });
  }
  async function task(run: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await run();
    } catch (e) {
      setError(
        axios.isAxiosError(e)
          ? String(
              e.response?.data?.message ?? "Opération indisponible. Réessayez.",
            )
          : "Opération indisponible.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function load(credential = session) {
    const { data } = await api.get<View>("/v1/contract-signing/session", {
      headers: headers(credential),
    });
    setView(data);
    if (data.documentKind) setKind(kindOf(data.documentKind));
    if (!pdf) {
      const file = await api.get("/v1/contract-signing/document/original", {
        responseType: "blob",
        headers: headers(credential),
      });
      setPdf(URL.createObjectURL(file.data));
    }
  }
  async function download(kind: string) {
    const result = await api.get(`/v1/contract-signing/document/${kind}`, {
      responseType: "blob",
      headers: headers(),
    });
    const url = URL.createObjectURL(result.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${w.file}-${kind}.${kind === "evidence" ? "json" : "pdf"}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }
  // Habillage de la charte (refonte du 14/10/2026) : parcours, textes et appels inchangés.
  const button =
    "inline-flex h-11 items-center justify-center rounded-pill bg-btn-primary px-6 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-45";
  const secondary =
    "inline-flex h-11 items-center justify-center rounded-pill border border-line bg-white px-5 text-sm font-medium text-body transition-all enabled:hover:border-primary-200 enabled:hover:shadow-navbar disabled:opacity-45";
  const input =
    "h-11 w-full rounded-xl border border-line bg-surface-light px-4 text-[15px] text-body outline-none focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50";
  return (
    <main className="min-h-screen bg-hero-indigo px-4 py-8 text-body">
      <div className="mx-auto max-w-4xl space-y-6">
        <img src="/logo-permio-eleve.png" alt="Permio" className="h-8 w-auto" />
        <header>
          <p className="mb-2 text-eyebrow uppercase text-primary-600">
            {w.header}
          </p>
          <h1 className="text-title-md text-heading sm:text-title-lg">{w.title}</h1>
          <p className="mt-2 text-sm text-secondary">
            Votre consentement est personnel. Vous pouvez télécharger le
            document ou refuser de le signer.
          </p>
        </header>
        {error && (
          <p role="alert" className="rounded-row bg-danger-light p-4 text-sm text-danger-dark">
            {error}
          </p>
        )}
        {!session ? (
          <section className="space-y-4 rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
            {!invite && !challenge ? (
              fromPortal ? (
                <p>
                  Demandez un nouveau code depuis votre espace élève.{" "}
                  <a className="font-medium text-primary-600 underline" href="/">
                    Retourner à mon espace élève
                  </a>
                </p>
              ) : (
                <p>{w.reopen}</p>
              )
            ) : !challenge ? (
              <>
                <h2 className="text-title-sm text-heading">
                  Vérifier votre adresse e-mail
                </h2>
                <p>
                  Nous allons envoyer un code à l’adresse du signataire.
                  L’ouverture du lien ne signe aucun document.
                </p>
                <button
                  className={button}
                  disabled={busy}
                  onClick={() =>
                    void task(async () => {
                      const { data } = await post("access", { token: invite });
                      setChallenge(data.challengeId);
                      setDestination(data.email);
                    })
                  }
                >
                  Recevoir mon code
                </button>
              </>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  void task(async () => {
                    const { data } = await post("verify", {
                      challengeId: challenge,
                      code,
                    });
                    setSession(data.session);
                    setCode("");
                    await load(data.session);
                  });
                }}
              >
                <p>Code envoyé à {destination}. Il expire dans dix minutes.</p>
                <label className="block">
                  Code à six chiffres
                  <input
                    className={input}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </label>
                <button className={button} disabled={busy || code.length !== 6}>
                  {w.consult}
                </button>
                <button
                  type="button"
                  className={secondary}
                  disabled={busy}
                  onClick={() => {
                    setChallenge("");
                    setCode("");
                  }}
                >
                  Demander un autre code
                </button>
              </form>
            )}
          </section>
        ) : (
          view && (
            <>
              <section className="rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
                <h2 className="text-title-sm text-heading">{view.documentName}</h2>
                <p className="mt-1 text-sm text-secondary">
                  {view.signer.name} · {roleName[view.signer.role]}
                </p>
                <p className="mt-2 break-all text-xs text-muted">
                  Empreinte du document présenté : {view.documentHash}
                </p>
                {pdf && (
                  <>
                    <a
                      className="my-4 inline-block font-medium text-primary-600 underline"
                      href={pdf}
                      download={view.documentName}
                    >
                      {w.download}
                    </a>
                    <iframe
                      title={w.frame}
                      src={pdf}
                      className="h-[65vh] w-full rounded-row border border-line"
                    />
                  </>
                )}
              </section>
              {view.state === "completed" ? (
                <section className="space-y-4 rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
                  <h2 className="text-title-sm text-heading">
                    Toutes les signatures sont enregistrées
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {[
                      ["signed", w.signed],
                      ["certificate", "Attestation"],
                      ["evidence", "Dossier de preuve"],
                    ].map(([kind, label]) => (
                      <button
                        key={kind}
                        className={secondary}
                        disabled={busy}
                        onClick={() => void task(() => download(kind))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {fromPortal && (
                    <a className="font-medium text-primary-600 underline" href="/">
                      Retourner à mon espace élève
                    </a>
                  )}
                </section>
              ) : view.signer.state === "signed" ? (
                <section className="space-y-3 rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
                  <h2 className="text-title-sm text-heading">
                    Votre signature est enregistrée
                  </h2>
                  <p>
                    Le document final sera disponible après les autres
                    signatures. Un e-mail vous donnera accès à votre copie.
                  </p>
                  <button
                    className={secondary}
                    disabled={busy}
                    onClick={() => void task(() => load())}
                  >
                    Actualiser le suivi
                  </button>
                  {fromPortal && (
                    <a className="ml-3 font-medium text-primary-600 underline" href="/">
                      Retourner à mon espace élève
                    </a>
                  )}
                </section>
              ) : view.state === "pending" ? (
                <form
                  className="space-y-5 rounded-card bg-white p-6 shadow-card max-sm:p-[18px]"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void task(async () => {
                      const { data } = await post("sign", {
                        name,
                        documentHash: view.documentHash,
                        read,
                        electronic,
                        authority,
                      });
                      setView(data);
                    });
                  }}
                >
                  <h2 className="text-title-sm text-heading">Votre consentement</h2>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={read}
                      onChange={(e) => setRead(e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                    />
                    {view.consents.read}
                  </label>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={electronic}
                      onChange={(e) => setElectronic(e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                    />
                    {view.consents.electronic}
                  </label>
                  {view.signer.role !== "student" && (
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={authority}
                        onChange={(e) => setAuthority(e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 accent-primary"
                      />
                      {view.signer.role === "guardian"
                        ? view.consents.guardian
                        : view.consents.school}
                    </label>
                  )}
                  <label className="block">
                    Saisissez votre nom complet : {view.signer.name}
                    <input
                      className={input}
                      autoComplete="name"
                      maxLength={200}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </label>
                  <p className="text-sm text-secondary">
                    Votre consentement, la date serveur, votre adresse IP et les
                    informations du navigateur seront conservés dans le dossier
                    de preuve accessible aux parties. Procédé de signature
                    simple par {fromPortal ? "espace élève" : "lien"} et code
                    e-mail.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      className={button}
                      disabled={
                        busy ||
                        !pdf ||
                        !read ||
                        !electronic ||
                        (view.signer.role !== "student" && !authority) ||
                        name.trim() !== view.signer.name.trim()
                      }
                    >
                      {w.sign}
                    </button>
                    <button
                      type="button"
                      className={secondary}
                      disabled={busy}
                      onClick={() => setDeclineReview(true)}
                    >
                      Refuser de signer
                    </button>
                  </div>
                  {declineReview && (
                    <div className="space-y-3 rounded-row bg-warn-soft p-4 text-sm text-warn-fg">
                      <p>
                        Ce refus arrête cette demande pour tous les signataires.
                        Le document et les consentements déjà donnés restent
                        conservés.
                      </p>
                      <button
                        type="button"
                        className={secondary}
                        disabled={busy}
                        onClick={() =>
                          void task(async () => {
                            await post("decline", {});
                            setView({ ...view, state: "declined" });
                          })
                        }
                      >
                        Confirmer mon refus
                      </button>
                      <button
                        type="button"
                        className="ml-3 font-medium underline"
                        onClick={() => setDeclineReview(false)}
                      >
                        {w.back}
                      </button>
                    </div>
                  )}
                </form>
              ) : (
                <p className="rounded-card bg-white p-6 shadow-card max-sm:p-[18px]">
                  Cette demande est terminée ou n’est plus disponible. Contactez
                  votre auto-école.
                </p>
              )}
            </>
          )
        )}
        <footer className="text-xs leading-relaxed text-secondary">
          Les signatures des personnes et le scellement technique du dossier
          sont distincts. Permio ne présente pas ce parcours comme une signature
          qualifiée.
        </footer>
      </div>
    </main>
  );
}
